import React from 'react';
import { DollarSign, MessageSquare, Clock, CheckCircle, Smile, Building, TrendingUp, TrendingDown, Calendar, Layers, PieChart } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const DashboardCards = ({ metrics, children }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(val);
  };

  // Calculate profit
  const totalProfit = metrics.totalRevenue - metrics.totalWorkloadCost;
  const profitRate = metrics.totalPlanGroups > 0 
    ? Math.round((metrics.profitableGroups / metrics.totalPlanGroups) * 100) 
    : 0;

  const cards = [
    {
      label: 'Total Revenue',
      value: formatCurrency(metrics.totalRevenue),
      subValue: 'Annual Gross Income',
      icon: DollarSign,
      trend: '+12.5%',
      trendUp: true,
      color: 'from-emerald-500 to-teal-500',
      iconBg: isDark ? 'bg-emerald-500/10' : 'bg-emerald-100',
      iconColor: 'text-emerald-500'
    },
    {
      label: 'Total Workload Cost',
      value: formatCurrency(metrics.totalWorkloadCost),
      subValue: 'Emails + Handle + Meetings',
      icon: DollarSign,
      trend: '+8.2%',
      trendUp: false,
      color: 'from-red-500 to-orange-500',
      iconBg: isDark ? 'bg-red-500/10' : 'bg-red-100',
      iconColor: 'text-red-500'
    },
    {
      label: 'Gross Margin Estimate',
      value: formatCurrency(totalProfit),
      subValue: `${metrics.avgMargin?.toFixed(1) || 0}% avg gross margin`,
      icon: PieChart,
      trend: totalProfit >= 0 ? 'Profitable' : 'Loss',
      trendUp: totalProfit >= 0,
      color: totalProfit >= 0 ? 'from-emerald-500 to-green-500' : 'from-red-500 to-orange-500',
      iconBg: totalProfit >= 0 ? (isDark ? 'bg-emerald-500/10' : 'bg-emerald-100') : (isDark ? 'bg-red-500/10' : 'bg-red-100'),
      iconColor: totalProfit >= 0 ? 'text-emerald-500' : 'text-red-500'
    },
    {
      label: 'Plan Groups',
      value: (metrics.totalPlanGroups || 0).toString(),
      subValue: `${metrics.totalPlans || 0} total plans`,
      icon: Layers,
      trend: `${profitRate}% profitable`,
      trendUp: profitRate >= 50,
      color: 'from-blue-500 to-cyan-500',
      iconBg: isDark ? 'bg-blue-500/10' : 'bg-blue-100',
      iconColor: 'text-blue-500'
    },
    {
      label: 'Email Cost',
      value: formatCurrency(metrics.totalMessageCost),
      subValue: '$10 per email',
      icon: MessageSquare,
      trend: '+8.2%',
      trendUp: false,
      color: 'from-purple-500 to-violet-500',
      iconBg: isDark ? 'bg-purple-500/10' : 'bg-purple-100',
      iconColor: 'text-purple-500'
    },
    {
      label: 'Handle Time Cost',
      value: formatCurrency(metrics.totalHandleTimeCost),
      subValue: 'Based on hourly rate',
      icon: Clock,
      trend: '-3.1%',
      trendUp: true,
      color: 'from-indigo-500 to-violet-500',
      iconBg: isDark ? 'bg-indigo-500/10' : 'bg-indigo-100',
      iconColor: 'text-indigo-500'
    },
    {
      label: 'Meeting Cost',
      value: formatCurrency(metrics.totalMeetingCost || 0),
      subValue: `${metrics.totalMeetings || 0} meetings total`,
      icon: Calendar,
      trend: 'New',
      trendUp: true,
      color: 'from-amber-500 to-yellow-500',
      iconBg: isDark ? 'bg-amber-500/10' : 'bg-amber-100',
      iconColor: 'text-amber-500'
    },
    {
      label: 'Client NPS Score',
      value: metrics.avgNPS > 0 ? `+${metrics.avgNPS.toFixed(0)}` : metrics.avgNPS.toFixed(0),
      subValue: `${metrics.avgCompliance?.toFixed(0) || 0}% compliance`,
      icon: Smile,
      trend: '+5.0%',
      trendUp: true,
      color: metrics.avgNPS >= 30 ? 'from-emerald-500 to-green-500' : metrics.avgNPS >= 0 ? 'from-cyan-500 to-teal-500' : 'from-red-500 to-orange-500',
      iconBg: metrics.avgNPS >= 30 ? (isDark ? 'bg-emerald-500/10' : 'bg-emerald-100') : metrics.avgNPS >= 0 ? (isDark ? 'bg-cyan-500/10' : 'bg-cyan-100') : (isDark ? 'bg-red-500/10' : 'bg-red-100'),
      iconColor: metrics.avgNPS >= 30 ? 'text-emerald-500' : metrics.avgNPS >= 0 ? 'text-cyan-500' : 'text-red-500'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card) => (
        <div 
          key={card.label} 
          data-testid={`dashboard-card-${card.label.toLowerCase().replace(/\s+/g, '-')}`}
          className="relative p-5 rounded-[10px] border border-[#E5E5E5] bg-white card-hover overflow-hidden"
          style={{ boxShadow: 'var(--card-shadow)' }}
        >
          <div className="flex items-start justify-between mb-3">
            <div className={`p-2 rounded-lg ${card.iconBg}`}>
              <card.icon className={`w-4 h-4 ${card.iconColor}`} />
            </div>
            <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold
              ${card.trendUp 
                ? 'bg-emerald-50 text-emerald-700' 
                : 'bg-red-50 text-red-600'}`}>
              {card.trendUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              <span>{card.trend}</span>
            </div>
          </div>
          <div>
            <h3 className="text-xl font-bold tracking-tight mb-0.5 text-[#171717]">{card.value}</h3>
            <p className="text-sm font-medium text-[#525252]">{card.label}</p>
            <p className="text-xs mt-1.5 text-[#A3A3A3]">{card.subValue}</p>
          </div>
        </div>
      ))}
      {children}
    </div>
  );
};

export default DashboardCards;
